import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  createAdvertismentService,
  getAdvertisementsService,
  updateAdvertisementService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const CampaignModal = ({ isOpen, onClose, campaign }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { productList } = useSelector((state) => state.products);
  const { categoryList } = useSelector((state) => state.categories);

  const [formData, setFormData] = useState({
    image: null,
    imagePreview: null,
    url: "",
    product_id: "",
    category_id: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (campaign) {
      setFormData({
        image: campaign?.image ? campaign.image[0] : null,
        imagePreview: campaign?.image ? campaign.image : null,
        url: campaign?.url || "",
        product_id: campaign?.product_id || "",
        category_id: campaign?.category_id || "",
      });
    } else {
      setFormData({
        image: null,
        imagePreview: null,
        url: "",
        product_id: "",
        category_id: "",
      });
    }
    setErrors({});
  }, [campaign, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      let updated = { ...prev, [field]: value };

      if (field === "product_id" && value) {
        updated.category_id = "";
      }

      if (field === "category_id" && value) {
        updated.product_id = "";
      }

      return updated;
    });

    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, image: "Invalid file type." }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          image: "File size must be less than 5MB",
        }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: file,
          imagePreview: { url: reader.result, name: file.name },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeMediaFile = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

  const handleSave = () => {
    if (!formData?.image) {
      setErrors({ image: "A media file is required" });
      return;
    }

    if (!formData.product_id && !formData.category_id) {
      setErrors({ relation: "يجب اختيار منتج أو فئة" });
      return;
    }

    try {
      const dataToSend = new FormData();
      dataToSend.append("image", formData.image);
      dataToSend.append("url", formData.url);
      dataToSend.append("type", "ad");

      if (formData.product_id) {
        dataToSend.append("product_id", formData.product_id);
      }

      if (formData.category_id) {
        dataToSend.append("category_id", formData.category_id);
      }

      if (campaign) {
        dataToSend.append("_method", "PUT");

        dispatch(
          updateAdvertisementService({
            id: campaign?.id,
            formData: dataToSend,
          }),
        ).then((response) => {
          if (
            response?.payload?.status === 200 ||
            response?.payload?.status === 201
          ) {
            toast.success("تم التعديل بنجاح !");
            dispatch(getAdvertisementsService({ page: 1, type: "ad" }));
            onClose();
          }
        });
      } else {
        dispatch(createAdvertismentService({ data: dataToSend })).then(
          (response) => {
            if (
              response?.payload?.status === 200 ||
              response?.payload?.status === 201
            ) {
              toast.success("تمت الإضافة بنجاح !");
              dispatch(getAdvertisementsService({ page: 1, type: "ad" }));
              onClose();
            }
          },
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold">
            {campaign ? t("editCampaign") : t("createNewCampaign")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* URL */}
          <div>
            <label className="block text-sm font-medium mb-2">
              رابط الإعلان (URL)
            </label>
            <input
              type="url"
              className="w-full p-2 border rounded-md dark:bg-gray-700"
              placeholder="https://example.com"
              value={formData.url}
              onChange={(e) => handleInputChange("url", e.target.value)}
            />
          </div>

          {/* Product Select */}
          <div>
            <label className="block text-sm font-medium mb-2">
              اختيار منتج
            </label>
            <select
              className="w-full p-2 border rounded-md"
              value={formData.product_id}
              onChange={(e) => {
                handleInputChange("product_id", e.target.value);
                handleInputChange("category_id", "");
              }}
            >
              <option value="">اختر منتج</option>
              {productList?.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.translations?.ar?.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category Select */}
          <div>
            <label className="block text-sm font-medium mb-2">اختيار فئة</label>
            <select
              className="w-full p-2 border rounded-md"
              value={formData.category_id}
              onChange={(e) => {
                handleInputChange("category_id", e.target.value);
                handleInputChange("product_id", "");
              }}
            >
              <option value="">اختر فئة</option>
              {categoryList?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.translations?.ar?.name}
                </option>
              ))}
            </select>
          </div>

          {errors?.relation && (
            <p className="text-sm text-red-600">{errors.relation}</p>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t("uploadMediaFile")}
            </label>

            {!formData.imagePreview ? (
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer"
                onClick={() => document.getElementById("media-upload")?.click()}
              >
                <Icon name="Upload" size={32} className="mx-auto mb-2" />
                <p className="text-sm text-gray-500">{t("dragDropOrClick")}</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="media-upload"
                />
              </div>
            ) : (
              <div className="relative border rounded-lg p-2">
                <img
                  src={formData?.imagePreview?.url || formData.imagePreview}
                  alt="Preview"
                  className="w-full h-48 object-contain rounded"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={removeMediaFile}
                  className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            )}

            {errors?.image && (
              <p className="text-sm text-red-600 mt-1">{errors?.image}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t gap-3">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave}>
            {campaign ? t("updateCampaign") : t("launchCampaign")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;
